// What the signed-in PLATFORM STAFF member may do, as the login response said.
//
// Written by redux/authSlice.js from `user.platformStaff.permissions` (an
// `{ id: true }` map) so the sidebar and route guards are right on the first
// paint, and wiped with the rest of the session at logout. Super Admin never
// has an entry here — `isSuperAdmin` short-circuits every check instead.
//
// SECURITY: presentation only. Every support socket event and REST route
// re-resolves the permission live from the database
// (Backend/api/services/platformPermissionService.js), so editing this key in
// devtools changes what the UI draws and nothing else.

import { PLATFORM_NAV } from '../config/platformNav';

const PERMISSIONS_KEY = 'PlatformPermissions';
const ROLE_NAME_KEY = 'PlatformRoleName';
// Mirror of the `support.queueScoping` feature flag ('true' | 'false'), written
// by redux/featureSlice.js whenever the flag list loads or the switch is
// toggled. The sync helpers below run outside React (App.jsx's "/" landing,
// the Login page's redirect) and cannot reach the store, so they read this.
// Missing (fresh install, or wiped by the logout localStorage.clear()) means
// ON — the stricter rule — which is also the flag's default.
const QUEUE_SCOPING_KEY = 'SupportQueueScoping';

/** Persist what login returned for a platform_staff session. */
export const cachePlatformAccess = (profile) => {
  try {
    if (!profile) {
      localStorage.removeItem(PERMISSIONS_KEY);
      localStorage.removeItem(ROLE_NAME_KEY);
      return;
    }
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(profile.permissions || {}));
    localStorage.setItem(ROLE_NAME_KEY, profile.roleName || '');
  } catch {
    // A full localStorage must not break login.
  }
};

/** Drop the cached permissions (logout). Safe to call when nothing is stored. */
export const clearPlatformAccess = () => cachePlatformAccess(null);

/** The stored `{ id: true }` map, or an empty object when none is stored. */
export const getPlatformPermissions = () => {
  try {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/** The platform role's display name ("Support Agent"), or ''. */
export const getPlatformRoleName = () => {
  try {
    return localStorage.getItem(ROLE_NAME_KEY) || '';
  } catch {
    return '';
  }
};

/** Persist the `support.queueScoping` flag for the sync helpers below. */
export const cacheSupportQueueScoping = (on) => {
  try {
    localStorage.setItem(QUEUE_SCOPING_KEY, on ? 'true' : 'false');
  } catch {
    // A full localStorage must not break a flag fetch; the default (ON) wins.
  }
};

/** Last-known `support.queueScoping` status; true unless explicitly 'false'. */
export const isSupportQueueScopingCached = () => {
  try {
    return localStorage.getItem(QUEUE_SCOPING_KEY) !== 'false';
  } catch {
    return true;
  }
};

export const isSuperAdminSession = () => localStorage.getItem('isSuperAdmin') === 'true';
export const isPlatformStaffSession = () => localStorage.getItem('Role') === 'platform_staff';

/**
 * True when the session may do `id`. Super Admin passes everything; a platform
 * staff member passes only what their role grants. Anyone else fails closed.
 */
export const hasPlatformPermission = (id) => {
  if (isSuperAdminSession()) return true;
  if (!id || !isPlatformStaffSession()) return false;
  return getPlatformPermissions()[id] === true;
};

/** Every id in the list must be granted (or the session is Super Admin). */
export const hasAllPlatformPermissions = (ids = []) => {
  if (isSuperAdminSession()) return true;
  return ids.every((id) => hasPlatformPermission(id));
};

/** At least one id in the list is granted (or the session is Super Admin). */
export const hasAnyPlatformPermission = (ids = []) => {
  if (isSuperAdminSession()) return true;
  return ids.some((id) => hasPlatformPermission(id));
};

/**
 * May this session open / do `perm`? The name the sidebar, the route guards
 * and the pages share, so a reader sees one verb everywhere. Accepts one id
 * or a list (any one of them suffices). Super Admin always may.
 */
export const canAccess = (perm) =>
  (Array.isArray(perm) ? hasAnyPlatformPermission(perm) : hasPlatformPermission(perm));

/**
 * The permissions a support page really requires under the current
 * `support.queueScoping` setting. ON: the list as written. OFF: the per-queue
 * `support.queue.<queue>` ids drop out, so support.view / support.respond
 * alone open both queues — the same rule the server's canViewQueue /
 * canRespondQueue apply. Callers inside React pass the flag from the store
 * (redux/featureSlice.js isQueueScopingOn); the sync callers fall back to the
 * localStorage mirror.
 */
export const requiredSupportPerms = (ids = [], queueScopingOn = isSupportQueueScopingCached()) =>
  queueScopingOn ? ids : ids.filter((id) => !String(id).startsWith('support.queue.'));

/**
 * May this session open the given support queue? Mirrors the server's
 * canViewQueue: super_admin, or support.view AND (when queue scoping is on)
 * support.queue.<queue>.
 */
export const canViewSupportQueue = (queue, queueScopingOn = isSupportQueueScopingCached()) =>
  hasAllPlatformPermissions(
    requiredSupportPerms(['support.view', `support.queue.${queue}`], queueScopingOn)
  );

/**
 * May this session open one entry of config/platformNav.js? The single rule
 * the sidebar filters by and the "/" landing walks by, so the two can never
 * disagree about what a role sees. `superAdminOnly` entries have no catalogue
 * key at all; `queue` entries follow the support rule above; everything else
 * is a plain permission — and Super Admin passes all three.
 */
export const canOpenNavItem = (item, queueScopingOn = isSupportQueueScopingCached()) => {
  if (item.superAdminOnly) return isSuperAdminSession();
  if (item.queue) return canViewSupportQueue(item.queue, queueScopingOn);
  return canAccess(item.perm);
};

/**
 * The sidebar manifest reduced to what THIS session may open — items
 * filtered, sections with nothing left dropped (an empty section would still
 * render a header with a "0" count badge).
 */
export const visiblePlatformNav = (queueScopingOn = isSupportQueueScopingCached()) =>
  PLATFORM_NAV
    .map((section) => ({ ...section, items: section.items.filter((item) => canOpenNavItem(item, queueScopingOn)) }))
    .filter((section) => section.items.length > 0);

/**
 * Where a platform staff member lands on "/" (and after login): the first
 * page their role opens, or null when it opens none (the caller shows the
 * no-access page). Support queues come first when held — an agent's day
 * starts in the queue, whatever else the role lets them look at — then the
 * sidebar order: dashboard, tenants, patients, masters, audit, team. Super
 * Admin never comes through here (their home is fixed at /dashboard).
 */
export const firstAllowedRoute = (queueScopingOn = isSupportQueueScopingCached()) => {
  if (canViewSupportQueue('tenant', queueScopingOn)) return '/platform-support';
  if (canViewSupportQueue('patient', queueScopingOn)) return '/moc/support';
  const first = visiblePlatformNav(queueScopingOn)[0]?.items[0];
  return first ? first.path : null;
};
